import { CartLinesUpdateEvent } from '@shopify/events';
import { normalisePlugInventory } from './afs-configurator-utils.js?v=19092026012013';

const groupSetupItems = (items) => {
  const grouped = new Map();
  items.filter((item) => item?.id).forEach((item) => {
    const properties = { ...(item.properties || {}) };
    if (properties._afs_role !== 'mount') {
      delete properties._afs_cutout;
      if (properties._afs_role === 'fitted-plug' || properties._afs_role === 'mating-plug') properties._afs_role = 'plug';
    }
    const normalizedItem = { ...item, properties };
    const key = String(normalizedItem.id) + ':' + JSON.stringify(properties);
    const existing = grouped.get(key);
    if (existing) existing.quantity += Number(normalizedItem.quantity) || 0;
    else grouped.set(key, { ...normalizedItem, quantity: Number(normalizedItem.quantity) || 0 });
  });
  return [...grouped.values()];
};

/* =====================================================================
   AFS mount configurator
   UI reskin only — dimension maths, pricing and the cart payload are
   identical to the previous version. The plug-type selector is now a
   segmented button group backed by a hidden [data-afs-opening] input, so
   every downstream read of `.value` keeps working exactly as before.
   ===================================================================== */

const AFS_FALLBACK_GEOMETRY = {
  "SB50":         { width: 35.425, length: 48.522, height: 16.125, cable: 40,  allowance: 3 },
  "3 Pole SB50":  { width: 56.952, length: 48.522, height: 16.125, cable: 40,  allowance: 3 },
  "SB120":        { width: 44.95,  length: 64,     height: 20.875, cable: 70,  allowance: 3 },
  "SB175":        { width: 54.475, length: 79.875, height: 25.65,  cable: 90,  allowance: 3 },
  "3 Pole SB175": { width: 84.955, length: 79.875, height: 26.195, cable: 90,  allowance: 3 },
  "SB350":        { width: 70.25,  length: 108.25, height: 31.25,  cable: 110, allowance: 3 }
};

const AFS_OPENING_SHORT = {
  "SB50": "SB50", "SB120": "SB120", "SB175": "SB175", "SB350": "SB350", "3 Pole SB50": "3 Pole SB50", "3 Pole SB175": "3 Pole SB175"
};

const titleCase = (value) => {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
};

/* Turns a DOM position key into a human label. "plug" -> "Plug" (single-cutout
   mounts, unchanged), "left"/"middle"/"right" -> capitalised (unchanged), and
   the newer "plug-1"/"plug-2"/"plug-3" keys (flush mounts) -> "Plug 1" etc. */
const formatPositionName = (position, fallback) => {
  if (position === 'plug') return 'Plug';
  if (!position) return fallback;
  const numbered = position.match(/^plug-(\d+)$/);
  if (numbered) return 'Plug ' + numbered[1];
  return position.charAt(0).toUpperCase() + position.slice(1);
};

function watchStickyBar(bar) {
  if (!bar || !('IntersectionObserver' in window)) return null;
  const heading = [...document.querySelectorAll('h1,h2,h3,h4,h5,h6')].find((item) => item.textContent.trim().replace(/\s+/g, ' ') === 'Australian Made');
  const boundary = heading?.closest('section') || heading || document.querySelector('footer, [role="contentinfo"]');
  if (!boundary) return null;
  const observer = new IntersectionObserver(([entry]) => bar.classList.toggle('is-hidden', entry.isIntersecting), { threshold: 0 });
  observer.observe(boundary);
  return observer;
}

class AFSMountConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.closest('product-component')?.classList.add('afs-configurator-active');

    this.mountVariants = this.readJSON('[data-afs-mount-variants]', []);
    this.accessories = this.readJSON('[data-afs-accessories]', { plugs: {}, dustCovers: {} });

    this.geometry = { ...AFS_FALLBACK_GEOMETRY };
    for (const row of this.readJSON('[data-afs-plug-library]', [])) {
      const name = String(row.name || '').trim();
      const values = {
        width: Number(row.width), length: Number(row.length), height: Number(row.height),
        cable: Number(row.cable), allowance: Number(row.allowance)
      };
      if (name && Object.values(values).every(Number.isFinite)) this.geometry[name] = values;
    }

    this.labels = this.cleanLabels(this.readJSON('[data-afs-labels]', ''));
    this.positions = [...this.querySelectorAll('[data-afs-position]')];

    this.populateLabels();
    this.wireOpeningButtons();
    this.applyURL();
    this.wireUpgrades();
    this.wireAddonCards();

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-afs-plug]') && !event.target.checked) {
        const mating = this.control(this.positionFor(event.target), '[data-afs-mating]');
        if (mating) mating.checked = false;
      }
      this.render();
    });
    this.querySelector('[data-afs-add]').addEventListener('click', () => this.addSetup());
    this.querySelector('[data-afs-sticky-add]')?.addEventListener('click', () => this.querySelector('[data-afs-add]')?.click());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.syncConfirmation();
    });
    this.render();
    this.stickyBarObserver = watchStickyBar(this.querySelector('[data-afs-sticky-bar]'));
  }

  readJSON(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || JSON.stringify(fallback));
    } catch (error) {
      console.warn('AFS configurator data could not be read.', error);
      return fallback;
    }
  }

  positionFor(element) {
    const key = element.closest('[data-afs-position]')?.dataset.afsPosition
      || element.closest('[data-afs-position-label]')?.dataset.afsPositionLabel
      || element.closest('[data-afs-position-plugs-group]')?.dataset.afsPositionPlugsGroup
      || element.closest('[data-afs-position-covers-group]')?.dataset.afsPositionCoversGroup;
    return this.positions.find((row) => row.dataset.afsPosition === key);
  }

  control(row, selector) {
    if (!row) return null;
    const key = row.dataset.afsPosition;
    const containers = [row, this.querySelector('[data-afs-position-label="' + key + '"]'), this.querySelector('[data-afs-position-plugs-group="' + key + '"]'), this.querySelector('[data-afs-position-covers-group="' + key + '"]')];
    return containers.map((container) => container?.querySelector(selector)).find(Boolean) || null;
  }

  cleanLabels(value) {
    return [...new Set(
      String(value || '')
        .split(/\r?\n/)
        .map(v => v.toUpperCase().replace(/[^A-Z0-9 -]/g, '').trim().slice(0, 13))
        .filter(Boolean)
    )];
  }

  populateLabels() {
    for (const select of this.querySelectorAll('[data-afs-label]'))
      for (const label of this.labels) select.add(new Option(label, label));
  }

  /* Segmented plug-type buttons drive the hidden [data-afs-opening] input */
  wireOpeningButtons() {
    for (const row of this.positions) {
      const seg = row.querySelector('[data-afs-seg]');
      if (!seg) continue;
      seg.addEventListener('click', (event) => {
        const button = event.target.closest('[data-afs-op]');
        if (!button) return;
        this.setOpening(row, button.dataset.afsOp);
        this.render();
      });
    }
  }

  setOpening(row, value) {
    const input = row.querySelector('[data-afs-opening]');
    if (!input) return;
    input.value = value;
    for (const button of row.querySelectorAll('[data-afs-op]'))
      button.setAttribute('aria-pressed', String(button.dataset.afsOp === value));
  }

  openingValues(row) {
    return [...row.querySelectorAll('[data-afs-op]')].map(b => b.dataset.afsOp);
  }

  applyURL() {
    const params = new URLSearchParams(location.search);
    this.positions.forEach((row, index) => {
      const key = row.dataset.afsPosition || ['left', 'middle', 'right'][index];
      const opening = params.get(`afs_${key}`);
      const label = params.get(`afs_${key}_label`);
      if (opening && this.openingValues(row).includes(opening)) this.setOpening(row, opening);
      const cleaned = this.cleanLabels(label)[0];
      if (label && this.labels.includes(cleaned)) { const field = this.control(row, '[data-afs-label]'); if (field) field.value = cleaned; }
    });
  }

  wireUpgrades() {
    for (const button of this.querySelectorAll('[data-afs-upgrade-toggle]')) {
      button.addEventListener('click', () => {
        const type = button.dataset.afsUpgradeToggle;
        const panel = this.querySelector('[data-afs-upgrade-panel="' + type + '"]');
        if (!panel) return;

        const willOpen = panel.hidden;
        panel.hidden = !willOpen;
        button.setAttribute('aria-expanded', String(willOpen));
        button.classList.toggle('is-open', willOpen);
      });
    }
  }

  /* One collapsible add-on card per cutout */
  wireAddonCards() {
    for (const head of this.querySelectorAll('[data-afs-addon-toggle]')) {
      head.addEventListener('click', () => {
        const card = head.closest('[data-afs-addon-card]');
        const body = card?.querySelector('[data-afs-addon-body]');
        if (!body) return;
        const willOpen = body.hidden;
        body.hidden = !willOpen;
        head.setAttribute('aria-expanded', String(willOpen));
        card.classList.toggle('is-open', willOpen);
      });
      head.closest('[data-afs-addon-card]')?.classList.toggle('is-open', head.getAttribute('aria-expanded') === 'true');
    }
  }

  renderAddonCards(states) {
    for (const state of states) {
      const card = this.querySelector('[data-afs-addon-card="' + state.position + '"]');
      if (!card) continue;
      const size = card.querySelector('[data-afs-addon-size]');
      const count = card.querySelector('[data-afs-addon-count]');
      if (size) size.textContent = AFS_OPENING_SHORT[state.opening] || state.opening;
      if (count) {
        const added = (state.plugEnabled ? 1 : 0) + (state.matingEnabled ? 1 : 0) + (state.coverEnabled ? 1 : 0);
        count.textContent = added ? added + ' added' : 'Nothing added';
        count.classList.toggle('is-added', added > 0);
      }
    }
  }

  selectedVariant() {
    const id = this.querySelector('[data-afs-mount-colours] input:checked')?.value;
    return this.mountVariants.find(v => String(v.id) === String(id)) || this.mountVariants.find(v => v.available) || this.mountVariants[0];
  }

  positionState(row) {
    const opening = row.querySelector('[data-afs-opening]').value;
    const plugInput = this.control(row, '[data-afs-plug]');
    const matingInput = this.control(row, '[data-afs-mating]');
    const coverInput = this.control(row, '[data-afs-cover]');
    const plugEnabled = Boolean(plugInput?.checked && !plugInput.disabled);
    const matingEnabled = Boolean(plugEnabled && matingInput?.checked && !matingInput.disabled);
    const plugColour = this.control(row, '[data-afs-plug-colour]')?.value || 'GREY';
    const coverEnabled = Boolean(coverInput?.checked && !coverInput.disabled);
    return {
      position: row.dataset.afsPosition,
      opening,
      label: this.control(row, '[data-afs-label]').value,
      plugEnabled, matingEnabled, plugColour, coverEnabled
    };
  }

  renderAccessories(row) {
    const opening = row.querySelector('[data-afs-opening]').value;
    const key = row.dataset.afsPosition;
    const plugTarget = this.querySelector('[data-afs-position-plugs-group="' + key + '"] [data-afs-position-plugs]');
    const coverTarget = this.querySelector('[data-afs-position-covers-group="' + key + '"] [data-afs-position-covers]');
    const previous = {
      plug: plugTarget?.querySelector('[data-afs-plug]')?.checked,
      mating: plugTarget?.querySelector('[data-afs-mating]')?.checked,
      colour: plugTarget?.querySelector('[data-afs-plug-colour]')?.value,
      cover: coverTarget?.querySelector('[data-afs-cover]')?.checked
    };
    const colours = this.accessories.plugs[opening] || {};
    const availableColours = Object.entries(colours).filter(([, item]) => item?.id);
    const cover = this.accessories.dustCovers[opening];

    if (plugTarget) {
      let html = '';
      if (availableColours.length) {
        const isSB50 = opening === 'SB50';
        const chosen = availableColours.find(([colour, item]) => colour === previous.colour && item.available) || availableColours.find(([, item]) => item.available) || availableColours[0];
        const price = chosen[1]?.price;
        const fittedNote = 'Supplied loose — matches your cutout.';
        html = `<div class="afs-plug-group" data-afs-plug-row>
          <label class="afs-addon" data-afs-fitted-row><input type="checkbox" data-afs-plug ${previous.plug ? 'checked' : ''}><span class="afs-addon__body">
            <span class="afs-addon__title"><span>Add an ${opening} plug</span><span class="afs-addon__price">+${this.money(price)}</span></span>
            <span class="afs-addon__note">${fittedNote}</span>
            ${isSB50 ? `<select class="afs-addon__select" data-afs-plug-colour aria-label="SB50 plug colour">${availableColours.map(([colour, item]) => `<option value="${colour}" ${item.available ? '' : 'disabled'} ${chosen[0] === colour ? 'selected' : ''}>${titleCase(colour)}${item.available ? '' : ' (sold out)'}</option>`).join('')}</select>` : `<input type="hidden" data-afs-plug-colour value="${chosen[0]}">`}
          </span></label>
          <label class="afs-addon afs-addon--secondary" data-afs-mating-row><input type="checkbox" data-afs-mating ${previous.mating && previous.plug ? 'checked' : ''}><span class="afs-addon__body">
            <span class="afs-addon__title"><span>Mating plug for your cable</span><span class="afs-addon__price">+${this.money(price)}</span></span>
            <span class="afs-addon__note">The other half of the connection</span>
          </span></label>
          <div class="afs-plug-stock-line"><span class="afs-pair-stock" data-afs-plug-stock></span></div>
        </div>`;
      } else {
        html = `<div class="afs-addon afs-addon--off"><input type="checkbox" disabled><span class="afs-addon__body"><span class="afs-addon__title"><span>${/^3 Pole/.test(opening) ? "We don't sell 3 Pole plugs — fit your own" : 'No plug available for this cutout'}</span></span><span class="afs-addon__note">The cutout is made to suit.</span></span></div>`;
      }
      plugTarget.innerHTML = html;
    }
    if (coverTarget) {
      coverTarget.innerHTML = cover?.id ? `<label class="afs-addon${cover.available ? '' : ' afs-addon--off'}"><input type="checkbox" data-afs-cover ${previous.cover && cover.available ? 'checked' : ''} ${cover.available ? '' : 'disabled'}><span class="afs-addon__body"><span class="afs-addon__title"><span>Dust cover${cover.available ? '' : ' (sold out)'}</span><span class="afs-addon__price">+${this.money(cover.price)}</span></span><span class="afs-addon__note">Keeps grit out when unplugged</span></span></label>` : '';
    }
  }

  plugEntries() {
    return this.positions.map((row) => {
      const state = this.positionState(row);
      const item = this.accessories.plugs[state.opening]?.[state.plugColour];
      return {
        item,
        fitted: this.control(row, '[data-afs-plug]'),
        mating: this.control(row, '[data-afs-mating]'),
        stock: this.control(row, '[data-afs-plug-stock]'),
        fittedRow: this.control(row, '[data-afs-fitted-row]'),
        matingRow: this.control(row, '[data-afs-mating-row]')
      };
    }).filter((entry) => entry.item && entry.fitted);
  }

  renderMountSummary(states, total) {
    const variant = this.selectedVariant();
    const mountName = this.positions.length === 1 ? 'Single' : this.positions.length === 2 ? 'Double' : 'Triple';
    const colour = variant?.title || 'Selected';
    const visual = this.querySelector('[data-afs-mount-summary-visual]');
    const text = this.querySelector('[data-afs-mount-summary-text]');
    const extras = this.querySelector('[data-afs-mount-summary-extras]');
    const totalElement = this.querySelector('[data-afs-mount-summary-total]');
    const breakdown = this.querySelector('[data-afs-mount-summary-breakdown]');
    const escapeHTML = (value) => String(value).replace(/[&<>"']/g, (character) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[character]));
    if (visual) {
      visual.style.setProperty('--afs-slots', states.length);
      visual.innerHTML = states.map((state) => {
        const position = formatPositionName(state.position, 'Plug');
        return `<div class="afs-mount-summary__slot"><span>${escapeHTML(state.label || position)}</span><strong>${escapeHTML(AFS_OPENING_SHORT[state.opening] || state.opening)}</strong></div>`;
      }).join('');
    }
    if (text) text.textContent = `${colour} ${mountName} Mount · ${states.map((state) => AFS_OPENING_SHORT[state.opening] || state.opening).join(' | ')}`;
    if (totalElement) totalElement.textContent = this.money(total);
    if (extras) {
      const plugCount = states.reduce((count, state) => count + (state.plugEnabled ? 1 : 0) + (state.matingEnabled ? 1 : 0), 0);
      const coverCount = states.filter((state) => state.coverEnabled).length;
      const labels = states.map((state) => state.label).filter(Boolean);
      const items = [];
      if (labels.length) items.push('Labels: ' + labels.join(' | '));
      if (plugCount) items.push('Genuine plugs ×' + plugCount);
      if (coverCount) items.push('Dust covers ×' + coverCount);
      extras.textContent = items.length ? items.join(' · ') : 'Mount only — no optional upgrades selected.';
    }
    if (breakdown) {
      const additions = new Map();
      const add = (label, price) => {
        const key = label + ':' + Number(price || 0);
        const line = additions.get(key) || { label, price: Number(price || 0), quantity: 0 };
        line.quantity += this.quantity();
        additions.set(key, line);
      };

      states.forEach((state) => {
        const plug = this.accessories.plugs[state.opening]?.[state.plugColour];
        const plugName = (AFS_OPENING_SHORT[state.opening] || state.opening) + ' plug';
        if (state.plugEnabled) add(plugName, plug?.price);
        if (state.matingEnabled) add(plugName, plug?.price);
        if (state.coverEnabled) add((AFS_OPENING_SHORT[state.opening] || state.opening) + ' dust cover', this.accessories.dustCovers[state.opening]?.price);
      });

      if (!additions.size) {
        breakdown.hidden = true;
        breakdown.innerHTML = '';
      } else {
        breakdown.hidden = false;
        breakdown.innerHTML = '<span class="afs-mount-summary__breakdown-label">Added items</span><ul>' + [...additions.values()].map((line) =>
          '<li><span>' + line.quantity + ' × ' + escapeHTML(line.label) + '</span><strong>+' + this.money(line.price * line.quantity) + '</strong></li>'
        ).join('') + '</ul>';
      }
    }
  }

  render() {
    for (const row of this.positions) this.renderAccessories(row);
    normalisePlugInventory(this.plugEntries(), this.quantity());
    const states = this.positions.map(row => this.positionState(row));
    this.renderDimensions(states);
    this.renderAddonCards(states);

    const colourValue = this.querySelector('[data-afs-colour-value]');
    if (colourValue) colourValue.textContent = this.selectedVariant()?.title || '';
    const addButton = this.querySelector('[data-afs-add]');
    if (addButton) addButton.disabled = !this.selectedVariant()?.available;
    const stickyAdd = this.querySelector('[data-afs-sticky-add]');
    if (stickyAdd) stickyAdd.disabled = !this.selectedVariant()?.available;

    const summary = this.querySelector('[data-afs-config-summary]');
    if (summary) summary.textContent = states.map(s => AFS_OPENING_SHORT[s.opening] || s.opening).join(' / ');

    states.forEach((state, index) => {
      const foot = this.positions[index].querySelector('[data-afs-foot]');
      const g = this.geometry[state.opening];
      if (foot && g) foot.textContent = `${Math.round(g.width)} × ${Math.round(g.length)} mm`;
    });

    let total = Number(this.selectedVariant()?.price || 0);
    for (const state of states) {
      const plugUnits = (state.plugEnabled ? 1 : 0) + (state.matingEnabled ? 1 : 0);
      total += plugUnits * Number(this.accessories.plugs[state.opening]?.[state.plugColour]?.price || 0);
      if (state.coverEnabled) total += Number(this.accessories.dustCovers[state.opening]?.price || 0);
    }
    total *= this.quantity();
    this.querySelector('[data-afs-total]').textContent = this.money(total);
    this.renderMountSummary(states, total);
    const stickySummary = this.querySelector('[data-afs-sticky-summary]');
    const stickyTotal = this.querySelector('[data-afs-sticky-total]');
    if (stickySummary) stickySummary.textContent = states.map((state) => {
      const name = formatPositionName(state.position, 'Cutout');
      return name + ': ' + (AFS_OPENING_SHORT[state.opening] || state.opening);
    }).join(' · ');
    if (stickyTotal) stickyTotal.textContent = this.money(total);

    this.dispatchEvent(new CustomEvent('afs:configuration-change', { bubbles: true, detail: { positions: states } }));
    this.emitExpressCheckout();
  }

  emitExpressCheckout() {
    const lines = this.buildItems();
    this.dispatchEvent(new CustomEvent('afs:express-checkout', {
      bubbles: true,
      detail: { lines, enabled: Boolean(this.selectedVariant()?.available && lines[0]?.id) }
    }));
  }

  renderDimensions(states) {
    const records = states.map((state) => ({ name: state.opening, ...this.geometry[state.opening] })).filter((record) => Number.isFinite(record.width));
    const count = records.length;
    const width = Math.ceil(17 + records.reduce((sum, record) => sum + record.width, 0) + 20 * Math.max(count - 1, 0) + 17);
    const length = Math.ceil(Math.max(...records.map((record) => record.length)) + 3 + Math.max(...records.map((record) => record.cable)) + 17);
    const height = Math.ceil(Math.max(...records.map((record) => record.height + record.allowance)));
    const setText = (selector, value) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    };

    setText('[data-afs-width]', `${width} mm`);
    setText('[data-afs-length]', `${length} mm`);
    setText('[data-afs-height]', `${height} mm`);
    setText('[data-afs-dimension-summary]', `L ${length} mm · W ${width} mm · H ${height} mm`);
  }

  buildItems() {
    const variant = this.selectedVariant();
    const states = this.positions.map(row => this.positionState(row));
    const setupId = `AFS-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`.toUpperCase();

    const positionName = (position, index) => {
      if (position) return formatPositionName(position, position.charAt(0).toUpperCase() + position.slice(1));
      return ['Left', 'Middle', 'Right'][index] || 'Plug';
    };
    const properties = {
      'Configuration': states.map(s => s.opening).join(' / '),
      '_afs_setup_id': setupId,
      '_afs_role': 'mount'
    };
    states.forEach((state, index) => {
      const name = positionName(state.position, index);
      properties[`${name} cutout`] = state.opening;
      if (state.label) properties[`${name} label`] = state.label;
    });

    const quantity = this.quantity();
    const items = [{ id: Number(variant.id), quantity, properties }];
    for (const state of states) {
      const plug = this.accessories.plugs[state.opening]?.[state.plugColour];
      if (state.plugEnabled && plug?.id)
        items.push({ id: Number(plug.id), quantity, properties: { '_afs_setup_id': setupId, '_afs_role': 'fitted-plug' } });
      if (state.matingEnabled && plug?.id)
        items.push({ id: Number(plug.id), quantity, properties: { '_afs_setup_id': setupId, '_afs_role': 'mating-plug' } });
      if (state.coverEnabled) {
        const cover = this.accessories.dustCovers[state.opening];
        if (cover?.id) items.push({ id: Number(cover.id), quantity, properties: { '_afs_setup_id': setupId, '_afs_role': 'accessory' } });
      }
    }
    return groupSetupItems(items);
  }

  showConfirmation(setupId) {
    const confirmation = this.querySelector('[data-afs-confirm]');
    const list = this.querySelector('[data-afs-confirm-list]');
    if (!confirmation || !list) return;

    const variant = this.selectedVariant();
    const states = this.positions.map((row) => this.positionState(row));
    const addLine = (label, text) => {
      const item = document.createElement('li');
      const strong = document.createElement('b');
      strong.textContent = label;
      item.append(strong, ` — ${text}`);
      list.append(item);
    };
    const includedPlugs = states
      .filter((state) => state.plugEnabled)
      .map((state) => `${state.opening} genuine plug${state.opening === 'SB50' ? ` · ${state.plugColour}` : ''}`);
    const matingPlugs = states
      .filter((state) => state.matingEnabled)
      .map((state) => `${state.opening} mating plug${state.opening === 'SB50' ? ` · ${state.plugColour}` : ''}`);
    const includedCovers = states
      .filter((state) => state.coverEnabled)
      .map((state) => `${state.opening} dust cover`);

    list.replaceChildren();
    addLine('Mount', `${this.dataset.productTitle || 'Anderson mount'}, ${variant?.title || ''} — ${this.money(variant?.price || 0)}`);
    addLine('Plug', includedPlugs.length ? includedPlugs.join(' / ') : 'No plugs added');
    addLine('Mating plug', matingPlugs.length ? matingPlugs.join(' / ') : 'No mating plugs');
    addLine('Dust cover', includedCovers.length ? includedCovers.join(' / ') : 'No dust covers included');
    addLine('Overall size', this.querySelector('[data-afs-dimension-summary]')?.textContent || '—');
    addLine('Setup total', this.querySelector('[data-afs-total]')?.textContent || '—');

    confirmation.classList.add('afs-confirm--show');
    this.activeSetupId = setupId;
    clearInterval(this.confirmTimer);
    this.confirmTimer = setInterval(() => this.syncConfirmation(), 2500);
    confirmation.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async syncConfirmation() {
    if (!this.activeSetupId) return;
    try {
      const response = await fetch(this.dataset.cartUrl + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      const cart = await response.json();
      const present = cart.items?.some((item) => item.properties?._afs_setup_id === this.activeSetupId && item.properties?._afs_role === 'mount');
      if (!present) this.hideConfirmation();
    } catch {}
  }

  hideConfirmation() {
    this.querySelector('[data-afs-confirm]')?.classList.remove('afs-confirm--show');
    this.activeSetupId = null;
    clearInterval(this.confirmTimer);
  }

  disconnectedCallback() {
    clearInterval(this.confirmTimer);
  }

  async addSetup() {
    const button = this.querySelector('[data-afs-add]');
    const status = this.querySelector('[data-afs-status]');
    const items = this.buildItems();
    const setupId = items[0]?.properties?._afs_setup_id;
    const deferredCartUpdate = CartLinesUpdateEvent.createPromise();

    button.disabled = true;
    button.textContent = 'Adding setup…';
    status.textContent = '';

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferredCartUpdate.promise,
    }));

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items, sections: ['cart-drawer-section'] })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'The setup could not be added.');

      const cartResponse = await fetch(this.dataset.cartUrl + '.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('The setup was added, but the cart could not refresh.');
      const cart = await cartResponse.json();

      deferredCartUpdate.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: result.sections,
          didError: false,
          items: cart.items,
          source: 'afs-mount-configurator',
          itemCount: cart.item_count,
        },
      });
      this.showConfirmation(setupId);
    } catch (error) {
      deferredCartUpdate.reject(error);
      status.textContent = error?.message || 'The setup could not be added.';
    } finally {
      button.disabled = false;
      button.textContent = 'Add chosen setup to cart';
    }
  }

  quantity() {
    return Math.max(1, Math.floor(Number(this.querySelector('[data-afs-quantity]')?.value) || 1));
  }

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency:this.dataset.afsCurrency||window.Shopify?.currency?.active||'AUD' }).format(Number(cents || 0) / 100);
  }
}

if (!customElements.get('afs-mount-configurator')) customElements.define('afs-mount-configurator', AFSMountConfigurator);
